import { BOOKED_STATUSES } from "../../../api/client-sessions-summary/constants";
import { DASHBOARD_COLORS } from "../../../dashboard-framework/colors";
import { EXCLUDE_INTERNAL_SESSIONS_FILTER } from "../../../dashboard-framework/commonFilters";
import { AggregateQuerySpec, ChartSeriesConfig, SelectOption } from "../../../dashboard-framework/types";
import { FIRST_EEG_SERVICE, NFB_SERVICE, PSY_SERVICE, REPORTS_FIRST_SERVICE } from "../../../utils/constants";

// Métricas e constantes usadas por 2+ dashboards (ver README do framework, secção "Métricas partilhadas").
// REGRA: antes de criar qualquer query/métrica nova, procurar aqui e nos configs existentes; se a métrica
// já existir noutro dashboard, mover para este módulo e importar de ambos os sítios.

export const NFB_PSY_SERVICES = [NFB_SERVICE, PSY_SERVICE];

// Universo e fórmula da taxa de comparência (Zoho "% attendence rate", usada pelo reception-kpis e
// pelo clinic-service-team-kpis): done/(done+cancelled+missed)×100. staff_unavailable e estados futuros ficam fora.
export const ATTENDANCE_RATE_STATUSES = ["done", "missed", "cancelled"];

export const attendanceRatePct = (doneCount: number, cancelledCount: number, missedCount: number) => {
    const denominator = doneCount + cancelledCount + missedCount;
    if (denominator === 0) return null;
    const ratePct = (doneCount / denominator) * 100;
    return ratePct;
};

// Estados de fim de tratamento (Zoho "Treatment Success vs They Declined"), partilhados pelo
// sessions-treatments e pelo clinic-service-team-kpis. Sucesso = alta clínica / a pedido / esporádico.
export const SUCCESS_STATUSES = ["done", "client_decided_is_ok", "sporadic"];
export const DECLINED_STATUS = "cliente_gave_up";
export const FINISHED_STATUSES = [...SUCCESS_STATUSES, DECLINED_STATUS];

// % sucesso = sucessos ÷ (sucessos + desistências) × 100; null sem denominador (→ gap na linha).
export const successRatePct = (successCount: number, declinedCount: number) => {
    const denominator = successCount + declinedCount;
    if (denominator === 0) return null;
    const ratePct = (successCount / denominator) * 100;
    return ratePct;
};

export const DONE_AND_TO_COME_STATUSES = ["done", ...BOOKED_STATUSES];

// Everything on the agenda that is still standing — booked, confirmed, awaiting in clinic, in session and done.
// Cancelled, missed and staff_unavailable are out: they no longer happen.
export const EVALUATIONS_HAPPENING_STATUSES = [...BOOKED_STATUSES, "in_session", "done"];

export const TREATMENT_FIRST_SESSION_TO_COME_STATUSES = ["booked", "confirmed", "done"];

export const CLINIC_OPTIONS: Array<SelectOption> = [
    { value: 1, label: "Porto" },
    { value: 3, label: "Braga" },
    { value: 2, label: "Virtual - Fora da clínica" },
];

// "Tratamentos iniciados + por vir" / "tratamentos a começar": por tratamento, a primeira sessão de
// NFB/Psicologia (serviços 33/34) com status booked/confirmed/done — réplica exata do SQL da query table
// Zoho "first session of treatment" (relido em 2026-06-11; avaliações e consultas NÃO contam como início
// de tratamento). Usada pela Análise Diária e pelo Marketing Digital.
// Fábrica parametrizada pelos estados da primeira sessão — permite variantes (ex.: só "realizada")
// sem duplicar a estrutura da query.
export const treatmentsFirstSessionQueryForStatuses = (statuses: Array<string>): AggregateQuerySpec => {
    const query: AggregateQuerySpec = {
        entity: "sessions",
        aggregate: { min: "start_date" },
        groupBy: ["treatment"],
        fixedFilter: {
            _and: [
                { treatment: { _nnull: true } },
                { service: { _in: NFB_PSY_SERVICES } },
                { status: { _in: statuses } },
            ],
        },
        dataScope: "global",
    };
    return query;
};

export const treatmentsFirstSessionQuery = treatmentsFirstSessionQueryForStatuses(TREATMENT_FIRST_SESSION_TO_COME_STATUSES);

// Estados em que a primeira sessão conta como tratamento já iniciado (Análise Diária) e a união com os
// estados ainda por vir — também usada como universo da classificação por mix de serviços.
export const TREATMENT_FIRST_SESSION_STARTED_STATUSES = ["done", "awaiting_in_clinic", "in_session"];
export const TREATMENT_FIRST_SESSION_STARTED_OR_TO_COME_STATUSES = [...TREATMENT_FIRST_SESSION_STARTED_STATUSES, "booked", "confirmed"];

// "Tratamentos Iniciados" (cartão da Análise Diária e série mensal por tipo do Marketing Digital):
// primeira sessão NFB/Psi já iniciada — realizada, à espera na clínica ou em sessão.
export const treatmentsFirstStartedSessionQuery = treatmentsFirstSessionQueryForStatuses(TREATMENT_FIRST_SESSION_STARTED_STATUSES);

// Serviços NFB/Psi presentes nas sessões (realizadas ou por vir) de cada tratamento — alimenta a
// classificação "Só NFB / Só Psi / NFB + Psi" (drill da Análise Diária e série mensal do Marketing
// Digital). O call site restringe o universo via filtro (treatment: {_in} ou start_date: {_gte}).
export const treatmentsServiceMixQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["treatment", "service"],
    fixedFilter: {
        _and: [
            { treatment: { _nnull: true } },
            { service: { _in: NFB_PSY_SERVICES } },
            { status: { _in: TREATMENT_FIRST_SESSION_STARTED_OR_TO_COME_STATUSES } },
        ],
    },
    dataScope: "global",
};

export const TREATMENT_SERVICE_MIX_LABELS = { nfbOnly: "Só NFB", psyOnly: "Só Psi", both: "NFB + Psi" };

type TreatmentServiceAggregateRow = { treatment: number | string; service: number | string; count?: number | string };

export const treatmentServicesLabelByTreatmentId = (
    aggregateRows: Array<TreatmentServiceAggregateRow>,
    nfbServiceId: number,
    psyServiceId: number
) => {
    const serviceIdsByTreatmentId = new Map<number, Set<number>>();
    aggregateRows.forEach((aggregateRow) => {
        const treatmentId = Number(aggregateRow.treatment);
        const serviceIds = serviceIdsByTreatmentId.get(treatmentId) || new Set<number>();
        serviceIds.add(Number(aggregateRow.service));
        serviceIdsByTreatmentId.set(treatmentId, serviceIds);
    });
    const labelByTreatmentId = new Map<number, string>();
    serviceIdsByTreatmentId.forEach((serviceIds, treatmentId) => {
        const hasNfb = serviceIds.has(nfbServiceId);
        const hasPsy = serviceIds.has(psyServiceId);
        const label = hasNfb && hasPsy ? TREATMENT_SERVICE_MIX_LABELS.both : hasPsy ? TREATMENT_SERVICE_MIX_LABELS.psyOnly : TREATMENT_SERVICE_MIX_LABELS.nfbOnly;
        labelByTreatmentId.set(treatmentId, label);
    });
    return labelByTreatmentId;
};

// Sessões NFB/Psi realizadas + por vir por mês desde 2025 (gráfico mensal da Análise Diária e do
// dashboard Sessões e Tratamentos), sem sessões internas.
export const monthlySessionsQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    groupBy: ["year(start_date)", "month(start_date)", "service"],
    fixedFilter: {
        _and: [
            { status: { _in: DONE_AND_TO_COME_STATUSES } },
            { service: { _in: NFB_PSY_SERVICES } },
            { start_date: { _gte: "2025-01-01" } },
            EXCLUDE_INTERNAL_SESSIONS_FILTER,
        ],
    },
    dataScope: "global",
};

// "Avaliação qEEG - Primeira" sessions (svc 39), with no status restriction — the call site closes the
// window and the status. Used by digital-marketing (several cards) and by the shared "Avaliações marcadas
// próx. 6 dias úteis" gauge (see UpcomingEvaluationsGauges).
export const evaluationsCountQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    fixedFilter: { service: { _eq: FIRST_EEG_SERVICE } },
    dataScope: "global",
};

// "Consulta de Avaliação" sessions (svc 37), with no status restriction — the call site closes the window and
// the status. Base of the shared "Consultas de avaliação próx. 5 dias úteis" gauge.
export const reportsCountQuery: AggregateQuerySpec = {
    entity: "sessions",
    aggregate: { count: "*" },
    fixedFilter: { service: { _eq: REPORTS_FIRST_SERVICE } },
    dataScope: "global",
};

// Goals of both "next working days" gauges (the gauge derives its bands with ±20%).
export const EVALUATIONS_NEXT_WORK_DAYS_GOAL = 50;
export const REPORTS_NEXT_WORK_DAYS_GOAL = 25;

export const sessionsChartSeries: Array<ChartSeriesConfig> = [
    { key: "nfb", label: "NFB", color: DASHBOARD_COLORS.done, services: [NFB_SERVICE] },
    { key: "psy", label: "Psicologia", color: DASHBOARD_COLORS.orange, services: [PSY_SERVICE] },
    { key: "total", label: "Total NFB + Psi", color: DASHBOARD_COLORS.highlight, sumOfSeries: ["nfb", "psy"] },
];
